import { useState } from "react";
import { PowerPlantData, PowerContract } from "@/contexts/GameContext";
import { useGame } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  plant: PowerPlantData;
  isOwner: boolean;
}

const MOCK_CLIENTS = [
  { id: "client-001", name: "Tokyo Tech Solutions", type: "企業" },
  { id: "client-002", name: "Paris Manufacturing Co.", type: "製造業" },
  { id: "client-003", name: "London Finance Group", type: "金融" },
  { id: "client-004", name: "NYC Retail Chain", type: "小売" },
  { id: "client-005", name: "Singapore Logistics", type: "物流" },
];

export default function ContractPanel({ plant, isOwner }: Props) {
  const { addPowerContract } = useGame();
  const { isDark } = useTheme();
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [form, setForm] = useState({
    clientId: "client-001",
    clientName: "Tokyo Tech Solutions",
    contractedMW: 10,
    pricePerMWh: 120,
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date(Date.now() + 365 * 24 * 3600000).toISOString().split("T")[0],
  });

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const inputClass = isDark
    ? "bg-white/5 border-white/10 text-white placeholder-gray-600 focus:border-amber-400/50"
    : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-400";
  const divider = isDark ? "divide-white/5" : "divide-gray-100";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";

  const activeContracts = plant.powerContracts.filter((c) => c.status === "active");
  const totalContractedMW = activeContracts.reduce((sum, c) => sum + c.contractedMW, 0);
  const availableMW = plant.capacity - totalContractedMW;
  const totalMonthlyRevenue = activeContracts.reduce(
    (sum, c) => sum + c.contractedMW * c.pricePerMWh * 24 * 30, 0
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.contractedMW > availableMW) {
      setMsg({ text: `空き容量が不足しています（空き: ${availableMW} MW）`, type: "error" });
      return;
    }
    const ok = addPowerContract(plant.id, {
      ...form,
      status: "active",
    });
    if (ok) {
      setMsg({ text: `${form.clientName}との電力供給契約を締結しました！`, type: "success" });
      setShowForm(false);
    } else {
      setMsg({ text: "契約の締結に失敗しました", type: "error" });
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const getStatusBadge = (status: PowerContract["status"]) => {
    if (status === "active") return "bg-emerald-400/15 text-emerald-400";
    if (status === "pending") return "bg-amber-400/15 text-amber-400";
    return isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400";
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${isDark ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
        <div>
          <h2 className={`text-base font-bold ${textPrimary}`}>電力供給契約</h2>
          <p className={`text-xs ${textSecondary} mt-0.5`}>
            契約済: <span className={`font-bold ${textPrimary}`}>{totalContractedMW} MW</span>
            {" "}/ 空き: <span className={`font-bold ${availableMW > 0 ? "text-emerald-400" : "text-red-400"}`}>{availableMW} MW</span>
            {" "}/ 月次収益: <span className="text-emerald-400 font-bold">{(totalMonthlyRevenue / 1000).toFixed(0)}K coins</span>
          </p>
        </div>
        {isOwner && availableMW > 0 && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl bg-amber-400/15 text-amber-400 hover:bg-amber-400/25 border border-amber-400/30 transition-all cursor-pointer whitespace-nowrap font-semibold"
          >
            <i className={showForm ? "ri-close-line" : "ri-add-line"}></i>
            {showForm ? "キャンセル" : "新規契約"}
          </button>
        )}
      </div>

      {/* Message */}
      {msg && (
        <div className={`mx-6 mt-4 rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`}>
          <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {msg.text}
        </div>
      )}

      {/* Contract Form */}
      {showForm && isOwner && (
        <form onSubmit={handleSubmit} className={`mx-6 my-4 rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/3 border-white/8" : "bg-gray-50 border-gray-200"}`}>
          <h3 className={`text-sm font-bold ${textPrimary}`}>新規電力供給契約</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>契約先</label>
              <select
                value={form.clientId}
                onChange={(e) => {
                  const client = MOCK_CLIENTS.find((c) => c.id === e.target.value);
                  setForm({ ...form, clientId: e.target.value, clientName: client?.name || "" });
                }}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors cursor-pointer ${inputClass}`}
              >
                {MOCK_CLIENTS.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>供給量 (MW) — 空き: {availableMW} MW</label>
              <input
                type="number"
                value={form.contractedMW}
                onChange={(e) => setForm({ ...form, contractedMW: Number(e.target.value) })}
                min={1}
                max={availableMW}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>単価 (coins/MWh)</label>
              <input
                type="number"
                value={form.pricePerMWh}
                onChange={(e) => setForm({ ...form, pricePerMWh: Number(e.target.value) })}
                min={1}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div className={`rounded-lg p-3 ${isDark ? "bg-emerald-400/5 border border-emerald-400/15" : "bg-emerald-50 border border-emerald-200"}`}>
              <p className={`text-xs ${textSecondary} mb-1`}>月次収益予測</p>
              <p className="text-emerald-400 font-bold text-lg">
                {(form.contractedMW * form.pricePerMWh * 24 * 30).toLocaleString()} coins
              </p>
              <p className={`text-xs ${textSecondary}`}>{form.contractedMW} MW × {form.pricePerMWh} coins/MWh × 720h</p>
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>契約開始日</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>契約終了日</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3 text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-text-line mr-2"></i>
            契約を締結する
          </button>
        </form>
      )}

      {/* Contract List */}
      {plant.powerContracts.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-amber-400/10">
            <i className="ri-file-text-line text-2xl text-amber-400"></i>
          </div>
          <p className={`text-sm ${textSecondary}`}>電力供給契約がありません</p>
          {isOwner && (
            <button onClick={() => setShowForm(true)} className="mt-3 text-xs text-amber-400 hover:underline cursor-pointer">
              最初の契約を締結する →
            </button>
          )}
        </div>
      ) : (
        <div className={`divide-y ${divider}`}>
          {plant.powerContracts.map((contract) => {
            const monthlyRevenue = contract.contractedMW * contract.pricePerMWh * 24 * 30;
            const endDate = new Date(contract.endDate);
            const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            return (
              <div key={contract.id} className={`flex items-center gap-4 px-6 py-4 transition-colors ${rowHover}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                  <i className="ri-file-text-line text-amber-400 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className={`text-sm font-semibold ${textPrimary}`}>{contract.clientName}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap font-medium ${getStatusBadge(contract.status)}`}>
                      {contract.status === "active" ? "契約中" : contract.status === "pending" ? "審査中" : "終了"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className={textSecondary}>供給量: <span className={`font-semibold ${textPrimary}`}>{contract.contractedMW} MW</span></span>
                    <span className={textSecondary}>単価: <span className="font-semibold text-amber-400">{contract.pricePerMWh} coins/MWh</span></span>
                    <span className={textSecondary}>残り: <span className={`font-semibold ${daysLeft < 30 ? "text-red-400" : daysLeft < 90 ? "text-amber-400" : "text-emerald-400"}`}>{daysLeft}日</span></span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-emerald-400 font-bold text-sm whitespace-nowrap">+{(monthlyRevenue / 1000).toFixed(0)}K</p>
                  <p className={`text-xs ${textSecondary}`}>coins/月</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
